import { apiClient } from './client';

/**
 * Wire shape for the tenant cohort-baseline surfaces
 * (master-baseline-cohort-submit):
 *   - `GET  /api/v1/tenants/{id}/cohort-baseline/preview`      (stats)
 *   - `POST /api/v1/tenants/{id}/cohort-baseline/submissions`  (submit)
 *   - `GET  /api/v1/tenants/{id}/cohort-baseline/submissions`  (history)
 *
 * A tenant admin who lacks IRCAD-style master data mints their own:
 * filter captured sessions to a cohort (participant job title / years),
 * preview each metric's median/mean/p25/p75/N, then tag + submit the
 * chosen metrics for superadmin review.
 */

/** A cohort needs at least this many sessions before it can be
 *  submitted. Mirrors the api's `MIN_COHORT_SESSIONS` (authoritative
 *  gate is server-side; this drives the pre-submit affordance). */
export const MIN_COHORT_SESSIONS = 10;

export interface CohortFilter {
  procedure: string | null;
  title: string | null;
  min_years: number | null;
  max_years: number | null;
}

export interface MetricStat {
  metric: string;
  modality: string; // 'hand' | 'eeg' | 'both'
  direction: string; // 'higher' | 'lower' | 'none'
  unit: string | null;
  median: number | null;
  mean: number | null;
  p25: number | null;
  p75: number | null;
  n: number;
}

export interface CohortStats {
  filter: CohortFilter;
  session_count: number;
  participant_count: number;
  metrics: MetricStat[];
}

export interface CohortSubmissionCreate {
  procedure?: string | null;
  title?: string | null;
  min_years?: number | null;
  max_years?: number | null;
  /** Narrow to a single participant's sessions (UUID). */
  participant_user_id?: string | null;
  /** Narrow to a single recording (capture_sessions.session_id). */
  session_id?: string | null;
  cohort: string;
  metrics: string[];
  baseline_group?: string | null;
}

export interface CohortSubmission {
  id: string;
  tenant_id: string;
  /** Populated on the superadmin review queue; null on a tenant's own list. */
  tenant_slug: string | null;
  tenant_name: string | null;
  procedure_group: string | null;
  procedure_name: string | null;
  cohort: string;
  filter: CohortFilter;
  metrics: MetricStat[];
  session_count: number;
  participant_count: number;
  status: string; // 'pending' | 'approved' | 'rejected'
  submitted_by_user_id: string;
  reviewed_by_user_id: string | null;
  review_note: string | null;
  created_at: string;
  reviewed_at: string | null;
  /** Populated for pending rows on the superadmin queue. */
  would_create: number | null;
  would_skip: number | null;
}

export interface CohortOverlap {
  cohort: string;
  procedure_group: string | null;
  overlap: { metric: string; statistic: string }[];
  would_create: number;
  would_skip: number;
}

export async function getCohortOverlap(
  tenantId: string,
  args: {
    cohort: string;
    procedure?: string | null;
    baselineGroup?: string | null;
    metrics: string[];
  },
): Promise<CohortOverlap> {
  const params: Record<string, string | string[]> = {
    cohort: args.cohort,
    metrics: args.metrics,
  };
  if (args.procedure) params.procedure = args.procedure;
  if (args.baselineGroup) params.baseline_group = args.baselineGroup;
  const { data } = await apiClient.get<CohortOverlap>(
    `/api/v1/tenants/${tenantId}/cohort-baseline/overlap`,
    // Repeat array params (metrics=a&metrics=b) — FastAPI's list form.
    { params, paramsSerializer: { indexes: null } },
  );
  return data;
}

export interface PreviewFilter {
  procedure?: string | null;
  title?: string | null;
  minYears?: number | null;
  maxYears?: number | null;
  /** Narrow to a single participant's sessions (UUID). */
  participantUserId?: string | null;
  /** Narrow to a single recording (capture_sessions.session_id). */
  sessionId?: string | null;
}

export async function previewCohortBaseline(
  tenantId: string,
  filter: PreviewFilter = {},
): Promise<CohortStats> {
  const params: Record<string, string | number> = {};
  if (filter.procedure) params.procedure = filter.procedure;
  if (filter.title) params.title = filter.title;
  if (filter.minYears !== undefined && filter.minYears !== null) {
    params.min_years = filter.minYears;
  }
  if (filter.maxYears !== undefined && filter.maxYears !== null) {
    params.max_years = filter.maxYears;
  }
  if (filter.participantUserId) {
    params.participant_user_id = filter.participantUserId;
  }
  if (filter.sessionId) {
    params.session_id = filter.sessionId;
  }
  const { data } = await apiClient.get<CohortStats>(
    `/api/v1/tenants/${tenantId}/cohort-baseline/preview`,
    { params },
  );
  return data;
}

export interface CohortFilterOptions {
  titles: string[];
  years: number[];
}

/** One canonical cohort tier — the standardized, ordered performance
 *  vocabulary served by GET /cohort-tiers. `key` is stored; `label` is
 *  shown; `rank` orders novice→expert; `color` is the bar tint. */
export interface CohortTier {
  key: string;
  label: string;
  rank: number;
  color: string;
}

export async function getCohortTiers(): Promise<CohortTier[]> {
  const { data } = await apiClient.get<CohortTier[]>('/api/v1/cohort-tiers');
  return data;
}

export async function getCohortFilterOptions(
  tenantId: string,
  procedure?: string | null,
): Promise<CohortFilterOptions> {
  const params: Record<string, string> = {};
  if (procedure) params.procedure = procedure;
  const { data } = await apiClient.get<CohortFilterOptions>(
    `/api/v1/tenants/${tenantId}/cohort-baseline/filter-options`,
    { params },
  );
  return data;
}

export async function submitCohortBaseline(
  tenantId: string,
  body: CohortSubmissionCreate,
): Promise<CohortSubmission> {
  const { data } = await apiClient.post<CohortSubmission>(
    `/api/v1/tenants/${tenantId}/cohort-baseline/submissions`,
    body,
  );
  return data;
}

export async function listCohortSubmissions(
  tenantId: string,
): Promise<CohortSubmission[]> {
  const { data } = await apiClient.get<CohortSubmission[]>(
    `/api/v1/tenants/${tenantId}/cohort-baseline/submissions`,
  );
  return data;
}

// --- Superadmin review queue (PR3) ---

export interface CohortSubmissionReview {
  submission: CohortSubmission;
  baselines_created: number;
  baselines_skipped: number;
}

export async function listAllCohortSubmissions(
  status?: string | null,
): Promise<CohortSubmission[]> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  const { data } = await apiClient.get<CohortSubmission[]>(
    '/api/v1/master-baseline-submissions',
    { params },
  );
  return data;
}

export async function approveCohortSubmission(
  submissionId: string,
): Promise<CohortSubmissionReview> {
  const { data } = await apiClient.post<CohortSubmissionReview>(
    `/api/v1/master-baseline-submissions/${submissionId}/approve`,
  );
  return data;
}

export async function rejectCohortSubmission(
  submissionId: string,
  reviewNote?: string | null,
): Promise<CohortSubmission> {
  const { data } = await apiClient.post<CohortSubmission>(
    `/api/v1/master-baseline-submissions/${submissionId}/reject`,
    { review_note: reviewNote ?? null },
  );
  return data;
}
